import { ChangeEvent, FC, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "../inputs/DropdownMenu"
import { InputField } from "../inputs/InputField"

export type SyntheticNameMode = "ANONYMOUS" | "DEFAULT_POOL" | "CUSTOM_POOL"

export interface SyntheticFillersValue {
  synthetic_count: string
  synthetic_score_min: string
  synthetic_score_max: string
  synthetic_name_mode: SyntheticNameMode
  synthetic_name_pool: string // newline- or comma-separated
}

interface SyntheticFillersBuilderProps {
  value: SyntheticFillersValue
  onChange: (next: SyntheticFillersValue) => void
}

export const SyntheticFillersBuilder: FC<SyntheticFillersBuilderProps> = ({ value, onChange }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const set = (patch: Partial<SyntheticFillersValue>) => onChange({ ...value, ...patch })

  const handleField = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value: v } = e.target
    set({ [name]: v } as Partial<SyntheticFillersValue>)
  }

  // Show an error when a numeric field holds a negative value
  const isNegative = (v: string) => v !== "" && Number(v) < 0
  const negativeError = t("tournaments.form.syntheticFillers.negativeValueError") as string
  const countInvalid = isNegative(value.synthetic_count)
  const minScoreInvalid = isNegative(value.synthetic_score_min)
  const maxScoreInvalid = isNegative(value.synthetic_score_max)

  const enabled = parseInt(value.synthetic_count || "0", 10) > 0

  const customNamesParsed = useMemo(() => {
    if (value.synthetic_name_mode !== "CUSTOM_POOL") return []
    return value.synthetic_name_pool
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean)
  }, [value.synthetic_name_pool, value.synthetic_name_mode])

  const count = parseInt(value.synthetic_count || "0", 10)
  const customWarning =
    value.synthetic_name_mode === "CUSTOM_POOL" && enabled && customNamesParsed.length > 0 && customNamesParsed.length < count
      ? (t("tournaments.form.syntheticFillers.customNamesWarning", { names: customNamesParsed.length, bots: count }) as string)
      : null

  const nameModeOptions = [
    { value: "ANONYMOUS", name: t("tournaments.form.syntheticFillers.nameModeAnonymous") as string },
    { value: "DEFAULT_POOL", name: t("tournaments.form.syntheticFillers.nameModeDefaultPool") as string },
    { value: "CUSTOM_POOL", name: t("tournaments.form.syntheticFillers.nameModeCustomPool") as string }
  ]

  return (
    <div className="space-y-4">
      <InputField
        title={t("tournaments.form.syntheticFillers.countLabel") as string}
        type="number"
        name="synthetic_count"
        value={value.synthetic_count}
        onChange={handleField}
        placeholder={t("tournaments.form.syntheticFillers.countPlaceholder") as string}
        altLabelTwo={countInvalid ? negativeError : (t("tournaments.form.syntheticFillers.countHelp") as string)}
        invalid={countInvalid}
      />

      {enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              title={t("tournaments.form.syntheticFillers.minScoreLabel") as string}
              type="number"
              name="synthetic_score_min"
              value={value.synthetic_score_min}
              onChange={handleField}
              placeholder="100"
              altLabelTwo={minScoreInvalid ? negativeError : undefined}
              invalid={minScoreInvalid}
            />
            <InputField
              title={t("tournaments.form.syntheticFillers.maxScoreLabel") as string}
              type="number"
              name="synthetic_score_max"
              value={value.synthetic_score_max}
              onChange={handleField}
              placeholder="500"
              altLabelTwo={maxScoreInvalid ? negativeError : undefined}
              invalid={maxScoreInvalid}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">{t("tournaments.form.syntheticFillers.scoreRangeHelp")}</p>

          <DropdownMenu
            title={t("tournaments.form.syntheticFillers.namesLabel") as string}
            name="synthetic_name_mode"
            value={value.synthetic_name_mode}
            onChange={handleField}
            options={nameModeOptions}
            hideEmptyOption
            disableEmptyOption
          />

          {value.synthetic_name_mode === "CUSTOM_POOL" && (
            <div className="w-full form-control">
              <label className="label">
                <span className="label-text">{t("tournaments.form.syntheticFillers.customNamesLabel")}</span>
              </label>
              <textarea
                name="synthetic_name_pool"
                value={value.synthetic_name_pool}
                onChange={handleField}
                rows={6}
                className="textarea textarea-bordered w-full bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] font-mono text-sm"
                placeholder={"BigWinBob\nLuckyLuna\nSpinKing"}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t("tournaments.form.syntheticFillers.customNamesParsed", { count: customNamesParsed.length })}
              </p>
              {customWarning && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{customWarning}</p>}
            </div>
          )}
        </>
      )}
    </div>
  )
}
